import { Field, useFormikContext } from 'formik';
import camelCase from 'lodash.camelcase';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useCheckoutState } from '../../../utils/checkoutState';
import { getAssetUrl } from '../../../utils/config';
import Paragraph from '../../atoms/Paragraph';
import SubHeadline from '../../atoms/SubHeadline';
import OptionsField from '../../formik/OptionsField';
import AdyenCreditcardPayment from '../../payment/AdyenCreditcardPayment';
import AdyenIdealPayment from '../../payment/AdyenIdealPayment';
import AfterpayPayment from '../../payment/AfterpayPayment';
import KlarnaPayment from '../../payment/KlarnaPayment';
import RatepayPayment from '../../payment/RatepayPayment';
import DynamicFieldArea from '../DynamicFieldArea';
import { showDefaultError, showNotification } from '../Notifications';
import CreditCardForm from './CreditCardForm';
import './styles.scss';

type AdditionalPaymentMethodConfig = {
  i18nKey?: string;
  useI18nKeyAsIconKey?: boolean;
  iconKeys?: string[];
  form?: FunctionComponent;
};

const additionalConfig: Record<string, AdditionalPaymentMethodConfig> = {
  standard_invoice: { i18nKey: 'invoice' },
  standard_cod: { i18nKey: 'cod' },
  sa_cod: { i18nKey: 'cod' },
  sk_cod: { i18nKey: 'cod' },
  standard_creditcard: { i18nKey: 'creditCard', form: CreditCardForm, iconKeys: ['mastercard', 'visa'] },
  accounting: { i18nKey: 'accountingB2b' },
  paypal: { i18nKey: 'payPal', useI18nKeyAsIconKey: true },
  paypal_instant: { i18nKey: 'payPal', useI18nKeyAsIconKey: true },
  ratepay_invoice: { i18nKey: 'invoice', form: RatepayPayment, iconKeys: ['ratepay'] },
  ratepay_directdebit: { i18nKey: 'sepaDirectDebit', form: RatepayPayment, iconKeys: ['sepaDirectDebit', 'ratepay'] },
  afterpay_invoice: { i18nKey: 'invoice', form: AfterpayPayment, iconKeys: ['afterPay'] },
  computop_creditcard: { i18nKey: 'creditCard', iconKeys: ['mastercard', 'visa'] },
  computop_creditcard_mastercard: { i18nKey: 'mastercard', useI18nKeyAsIconKey: true },
  computop_creditcard_visa: { i18nKey: 'visa', useI18nKeyAsIconKey: true },
  computop_maestro: { i18nKey: 'debitCard', iconKeys: ['maestro'] },
  computop_bancontact: { i18nKey: 'bancontact', useI18nKeyAsIconKey: true },
  computop_payu: { i18nKey: 'payU', useI18nKeyAsIconKey: true },
  computop_paydirekt: { i18nKey: 'paydirekt', useI18nKeyAsIconKey: true },
  computop_postfinance: { i18nKey: 'postFinance', useI18nKeyAsIconKey: true },
  klarna_paylater: { i18nKey: 'invoice', form: KlarnaPayment, iconKeys: ['klarna'] },
  klarna_paynow_direct_debit: {
    i18nKey: 'sepaDirectDebit',
    form: KlarnaPayment,
    iconKeys: ['sepaDirectDebit', 'klarna'],
  },
  klarna_paynow_direct_bank_transfer: { i18nKey: 'klarnaPayNow', form: KlarnaPayment, iconKeys: ['klarna'] },
  klarna_installments: { i18nKey: 'klarnaInstallments', form: KlarnaPayment, iconKeys: ['klarna'] },
  arvato_creditcard: { i18nKey: 'creditCard', useI18nKeyAsIconKey: true, iconKeys: ['mastercard', 'visa'] },
  arvato_paypal: { i18nKey: 'payPal', useI18nKeyAsIconKey: true },
  arvato_ideal: { i18nKey: 'iDeal', useI18nKeyAsIconKey: true },
  amazon_pay: { i18nKey: 'amazonPay', useI18nKeyAsIconKey: true },
  adyen_creditcard: { i18nKey: 'creditCard', form: AdyenCreditcardPayment, iconKeys: ['mastercard', 'visa'] },
  adyen_ideal: { i18nKey: 'iDeal', useI18nKeyAsIconKey: true, form: AdyenIdealPayment },
  adyen_mobilepay: { i18nKey: 'mobilePay', useI18nKeyAsIconKey: true },
};

const PaymentOptions = ({ setLoading }: { setLoading: (isLoading: boolean) => void }) => {
  const { checkoutState, setPaymentMethod /* removeRecurringPaymentMethod */ } = useCheckoutState();
  const { formatMessage } = useIntl();
  const { values, setFieldValue } = useFormikContext<{ paymentMethod: string }>();
  const {
    orderOptions: { payment: paymentOptions },
    orderState,
  } = checkoutState;
  const [currentPaymentMethods, setCurrentPaymentMethods] = useState(paymentOptions?.generic);

  useEffect(() => {
    const nextPaymentMethods = paymentOptions?.generic;
    if (nextPaymentMethods && JSON.stringify(currentPaymentMethods) !== JSON.stringify(nextPaymentMethods)) {
      setCurrentPaymentMethods(nextPaymentMethods);
    }

    const paymentMethod =
      orderState.payment?.selected?.[0].paymentOptionKey ?? orderState.payment?.giftCards?.[0].paymentOptionKey;
    if (paymentMethod && paymentMethod !== values.paymentMethod) {
      setFieldValue('paymentMethod', paymentMethod);
    }
  }, [checkoutState]);

  const isPaymentDisabled = !orderState.addresses?.billing;

  const onChange = async (paymentOptionKey: string) => {
    setLoading(true);
    try {
      await setPaymentMethod({ paymentOptionKey });
    } catch (e) {
      console.error(e);
      showDefaultError();
    }
    setLoading(false);
  };

  const onChangeRecurring = async (recurringPaymentOptionId: string) => {
    const recurringOption = recurringOptions?.find(
      _recurringOption => recurringPaymentOptionId === _recurringOption.key
    );
    setLoading(true);
    try {
      await setPaymentMethod({
        paymentOptionKey: recurringOption!.paymentOptionKey,
        recurringPaymentOptionId,
      });
    } catch (e) {
      console.error(e);
      showDefaultError();
    }
    setLoading(false);
  };

  const genericOptions = useMemo(
    () =>
      currentPaymentMethods?.map(({ paymentOptionKey: key, isDisabled }) => {
        const {
          i18nKey,
          form: Form,
          useI18nKeyAsIconKey,
          iconKeys,
        } = additionalConfig[key] ?? {
          i18nKey: camelCase(key),
          useI18nKeyAsIconKey: true,
        };
        const label = formatMessage({ id: `payment.paymentMethod.options.${i18nKey}` });
        return {
          key,
          label,
          defaultIcon: useI18nKeyAsIconKey && getAssetUrl(`payment/${i18nKey}.svg`),
          defaultIcons: iconKeys?.map(iconKey => getAssetUrl(`payment/${iconKey}.svg`)),
          form: () => (
            <>
              {Form && <Form />}
              <DynamicFieldArea area="paymentOptions" conditionValues={{ paymentOptionKey: key }} />
            </>
          ),
          isDisabled: isPaymentDisabled || isDisabled,
          info:
            isDisabled &&
            !isPaymentDisabled &&
            formatMessage({ id: 'payment.paymentMethod.disabled' }, { paymentMethod: label }),
        };
      }) ?? [],
    [currentPaymentMethods, isPaymentDisabled]
  );

  const recurringOptions = paymentOptions?.recurring?.map(
    ({ recurringPaymentOptionId: key, paymentOptionKey, recurringPaymentInformation, isDisabled }) => {
      const { i18nKey, useI18nKeyAsIconKey, iconKeys } = additionalConfig[paymentOptionKey] ?? {
        i18nKey: camelCase(paymentOptionKey),
        useI18nKeyAsIconKey: true,
      };
      const label = formatMessage({ id: `payment.paymentMethod.options.${i18nKey}` });
      return {
        key,
        label: recurringPaymentInformation?.maskedCreditcardNumber
          ? `•••• ${recurringPaymentInformation.maskedCreditcardNumber.replaceAll('X', '')}`
          : label,
        defaultIcon: useI18nKeyAsIconKey && getAssetUrl(`payment/${i18nKey}.svg`),
        defaultIcons: iconKeys?.map(iconKey => getAssetUrl(`payment/${iconKey}.svg`)),
        paymentOptionKey,
        // onDelete: () => removeRecurringPaymentMethod({ id: key }),
        isDisabled: isPaymentDisabled || isDisabled,
        info:
          isDisabled &&
          !isPaymentDisabled &&
          formatMessage({ id: 'payment.paymentMethod.disabled' }, { paymentMethod: label }),
      };
    }
  );

  useEffect(() => {
    if (!isPaymentDisabled && !genericOptions.length) {
      showNotification({
        level: 'info',
        text: formatMessage({ id: 'payment.paymentMethod.notifications.noPaymentMethods' }),
        autoClose: true,
      });
    }

    if (!isPaymentDisabled && genericOptions.some(genericOption => genericOption.isDisabled)) {
      showNotification({
        level: 'info',
        text: formatMessage({ id: 'payment.paymentMethod.notifications.notAvailable' }),
        autoClose: true,
      });
    }
  }, []);

  return (
    <div className="payment-options">
      <SubHeadline>
        <FormattedMessage id="payment.paymentMethod.headline" />
      </SubHeadline>
      {isPaymentDisabled && (
        <Paragraph>
          <FormattedMessage id="payment.paymentMethod.noBillingAddress" />
        </Paragraph>
      )}
      {!!recurringOptions?.length && (
        <>
          <Paragraph>
            <FormattedMessage id="payment.subheadlineRecurring" />
          </Paragraph>
          <Field
            name="paymentMethod"
            component={OptionsField}
            options={recurringOptions}
            isVertical
            onChange={onChangeRecurring}
          ></Field>
          <Paragraph>
            <FormattedMessage id="payment.subheadlineOther" />
          </Paragraph>
        </>
      )}
      <Field
        name="paymentMethod"
        component={OptionsField}
        options={genericOptions}
        isVertical
        dataTestIdPrefix="paymentOptions"
        onChange={onChange}
      />
    </div>
  );
};

export default PaymentOptions;
